import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getStoreRewards } from '../../utils/service';
import ShareStoreDealsBackground from '../../images/share-store-deals-background.svg';
import TrashieLogo from '../../images/trashie-logo.webp';
import DownloadAppStore from '../../images/download-app-store.svg';
import DownloadGooglePlay from '../../images/download-google-play.svg';
import LoadingBar from '../../global/LoadingBar';

import './ShareDeal.scss';
import { APP_STORE_URL, PLAY_STORE_URL } from '../../config/externalURL';
import { ROUTE_REWARDS_V2 } from '../../config/routes';

const ShareDeal = () => {
  const { storeId, dealId } = useParams();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [storeRewards, setStoreRewards] = useState(null);
  const [dealData, setDealData] = useState(null);

  const loadStoreRewards = useCallback(async () => {
    if (!storeId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const { data, error: storeRewardsError } = await getStoreRewards(storeId, true);

    if (storeRewardsError || !data) {
      setIsLoading(false);
      if (storeRewardsError) {
        setError(storeRewardsError);
      }
      return;
    }

    const { store, rewards } = data;

    setStoreRewards(data);

    // If dealId is provided, find the specific deal
    if (dealId && rewards?.length) {
      const foundDeal = rewards.find((reward) => reward.uid === dealId);
      if (!foundDeal) {
        setError(
          'NOT_FOUND_ERROR',
        );
        setIsLoading(false);
        return;
      }
      setDealData(foundDeal);
    }

    if (dealId && !rewards?.length) {
      setError(
        'NOT_FOUND_ERROR',
      );
      setIsLoading(false);
      return;
    }

    if (!dealId && !store?.affiliateLink
      && !rewards?.find((reward) => reward?.affiliateLink)) {
      setError(
        'NOT_FOUND_ERROR',
      );
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  }, [storeId, dealId]);

  useEffect(() => {
    loadStoreRewards();
  }, [loadStoreRewards]);

  const merchantName = storeRewards?.store?.title || 'this brand';

  const getMobileOS = useCallback(() => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(ua)) return 'Android';
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'iOS';
    return 'unknown';
  }, []);

  const handleDownloadApp = () => {
    window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadGooglePlay = () => {
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  const handleContinue = () => {
    if (dealId && dealData?.affiliateLink) {
      window.location.href = dealData.affiliateLink;
      return;
    }

    const affiliateLink = storeRewards?.store?.affiliateLink ||
      storeRewards?.rewards?.find((reward) => reward?.affiliateLink)?.affiliateLink;

    if (affiliateLink) {
      window.location.href = affiliateLink;
    } else {
      setError(
        'NOT_FOUND_ERROR',
      );
    }
  };
  const handleExploreMoreDeals = () => {
    window.location.href = ROUTE_REWARDS_V2;
  };

  const renderContent = () => {
    if (error) {
      return (
        <div className="ShareDeal__emptyContainer">
          <h1 className="ShareDeal__title">Oops!</h1>
          <p className="ShareDeal__subtitle">
            Something went wrong.
            <br />
            {`The ${dealId ? 'deal' : 'store'} you're looking for is not available.`}
          </p>

          <button
            type="button"
            className="ShareDeal__blackButton"
            onClick={handleExploreMoreDeals}
          >
            Explore more deals
          </button>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="ShareDeal__emptyContainer">
          <LoadingBar />
        </div>
      );
    }

    const os = getMobileOS();
    const showIOS = os === 'iOS' || os === 'unknown';
    const showAndroid = os === 'Android' || os === 'unknown';

    return (
      <>
        <h1 className="ShareDeal__title">Download Our App</h1>

        <p className="ShareDeal__subtitle">Shop deals and earn points.</p>

        <div className="ShareDeal__buttonsContainer">
          {showIOS && (
            <button
              type="button"
              className="ShareDeal__blackButton"
              onClick={handleDownloadApp}
              aria-label="Download on the App Store"
            >
              <img
                src={DownloadAppStore}
                alt="Download on the App Store"
                className="ShareDeal__appStoreIcon"
              />
            </button>
          )}

          {showAndroid && (
            <button
              type="button"
              className="ShareDeal__blackButton"
              onClick={handleDownloadGooglePlay}
              aria-label="Get it on Google Play"
            >
              <img
                src={DownloadGooglePlay}
                alt="Get it on Google Play"
                className="ShareDeal__appStoreIcon"
              />
            </button>
          )}
        </div>

        <div className="ShareDeal__footerSection">
          <button
            type="button"
            className="ShareDeal__continueButton"
            onClick={handleContinue}
          >
            {`Continue with ${merchantName}`}
          </button>

          <p className="ShareDeal__disclaimer">
            You&apos;ll miss out on the savings and rewards.
          </p>
        </div>
      </>
    );
  };

  return (
    <div className="ShareDeal">
      <img
        src={ShareStoreDealsBackground}
        alt="background image"
        className="ShareDeal__background"
        aria-hidden="true"
      />

      <div className="ShareDeal__content">
        <div className="ShareDeal__card">
          <img
            src={TrashieLogo}
            alt="Trashie"
            className="ShareDeal__logo"
          />

          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ShareDeal;
